// Shared computation of an instructor's currently-missing requirements.
// This is the SINGLE source of truth used by the directory table,
// summary cards, filters, missing-info queue, and (mirrored logic) the
// Silver Sage Assistant. Do NOT read the legacy `action_required` free-text
// column for display — it is deprecated and only kept as a nullable field
// for backwards compatibility. Always compute from live instructor fields.

export type MissingInstructor = {
  id: string;
  status: string | null;
  phone: string | null;
  email: string | null;
  pay_rate_amount: number | null;
  hourly_rate: number | null;
  pay_rate_type?: string | null;
  payment_method: string | null;
  zelle_name?: string | null;
  zelle_contact?: string | null;
  teaching_agreement_signed: boolean | null;
  teaching_agreement_year?: number | null;
  background_check_completed: boolean | null;
  background_check_year?: number | null;
  standards_of_conduct_acknowledged: boolean | null;
  independent_contractor_ack?: boolean | null;
  w9_status: string | null;
  onboarding_status?: string | null;
};

export type MissingAssignment = {
  instructor_id: string;
  status: string | null;
};

/**
 * Returns the plain-English list of currently missing requirements for an
 * instructor, derived from live fields. Empty array = nothing missing.
 *
 * Every UI surface (directory row "Action Required", summary card counts,
 * Missing Information queue, filter dropdown, dashboard alerts, executive
 * brief, assistant responses) must call this function so results stay in
 * sync after any save.
 *
 * Rule summary (Standards of Conduct is folded into the Teaching Agreement
 * and is no longer tracked separately):
 * - Active (paid): contact info, assignment, pay/tax/Zelle, IC ack, W-9,
 *   teaching agreement, background check, onboarding.
 * - Volunteer (unpaid): contact info, assignment, teaching agreement,
 *   background check, onboarding. NO pay/tax/Zelle/IC/W-9.
 * - Everyone else: contact info only.
 */
export function getInstructorMissingRequirements(
  i: MissingInstructor,
  assignmentsForInstructor: MissingAssignment[] = [],
): string[] {
  // Only "active" (paid) and "volunteer" (unpaid) instructors need on-file paperwork.
  // Volunteers skip tax/pay/Zelle requirements; they still need the teaching
  // agreement and a current background check.
  const isActive = i.status === "active";
  const isVolunteer = i.status === "volunteer";
  const list: string[] = [];
  if (!i.phone || i.phone.replace(/\D/g, "").length < 7) list.push("phone");
  if (!i.email) list.push("email");
  if (!isActive && !isVolunteer) return list; // prospects/former/etc: only need contact info
  const hasAssignment = assignmentsForInstructor.some(
    (a) => a.instructor_id === i.id &&
      (a.status === "active" || a.status === "upcoming" || a.status === "proposed"),
  );
  if (!hasAssignment) list.push("class assignment");

  if (isActive) {
    if (!i.pay_rate_type && !(i.pay_rate_amount || i.hourly_rate)) list.push("pay arrangement");
    if (!(i.pay_rate_amount || i.hourly_rate)) list.push("pay rate");
    // Payment information: satisfied by any configured payment method
    // (e.g., Cash) or by a stored Zelle contact. A Zelle method requires
    // the Zelle contact to be filled in.
    const method = (i.payment_method ?? "").trim();
    const zelle = (i.zelle_contact ?? "").trim();
    const isZelle = method.toLowerCase().includes("zelle");
    const paymentOk = isZelle ? zelle.length > 0 : (method.length > 0 || zelle.length > 0);
    if (!paymentOk) list.push("Zelle information");
    if (i.w9_status !== "Received" && i.w9_status !== "Exempt or Not Required") list.push("W-9");
  }
  if (!i.teaching_agreement_signed) list.push("annual teaching agreement");
  if (!i.background_check_completed) list.push("current background check");
  if (i.onboarding_status && i.onboarding_status !== "complete") list.push("onboarding status");

  // Dedupe (pay arrangement / pay rate can overlap)
  return Array.from(new Set(list));
}

export function hasActionRequired(
  i: MissingInstructor,
  assignmentsForInstructor: MissingAssignment[] = [],
): boolean {
  return getInstructorMissingRequirements(i, assignmentsForInstructor).length > 0;
}
